/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, ILinkData, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect, ProspectType } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { EmailRegex, PhoneRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IVinhusetBusinessSignUpConfig, IVinhusetBusinessSignUpProps, IVinhusetBusinessSignUpResources } from './vinhuset-business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';
import { IBusinessSignUpRadioButtonOptionProps, BusinessSignUpRadioButtonOption } from './components/sign-up-radio-button';
import BusinessSignUpOption, { IBusinessSignUpOptionOptionProps } from './components/sign-up-option';

export interface IBusinessSignUpExtendedProps extends IVinhusetBusinessSignUpProps<IVinhusetBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
    radioButtonOptions?: IBusinessSignUpRadioButtonOptionProps[];
    radioButtonGroupClass?: string;
    selectOptions?: IBusinessSignUpOptionOptionProps[];
    placeholder?: string;
    disabled?: boolean;
    checkboxDisable?: string;
    error?: string;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    Checkbox = 'checkbox',
    RadioButton = 'radiobutton',
    Select = 'select',
    Label = 'label',
    AgreeToTerms = "agreeToTerms"
}

export enum InputID {
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    Comment = 'Comment',
    ServingPlace = 'ServingPlace',
    ServingPlacePhone = "ServingPlacePhone",
    ServingPlaceEmail = "ServingPlaceEmail",
    DeliveryFrequency = 'DeliveryFrequency',
    InvoiceAddress = 'InvoiceAddress',
    DeliveryAddress = 'DeliveryAddress',
    InvoiceType = 'InvoiceType',
    DeliveryInformation = 'DeliveryInformation',
    ContactPaymentLabel = 'ContactPaymentLabel',
    ContactPaymentName = 'ContactPaymentName',
    ContactPaymentLastName = 'ContactPaymentLastName',
    ContactPaymentEmail = 'ContactPaymentEmail',
    ContactPaymentPhone = 'ContactPaymentPhone',
    ContactOrderPlacementLabel = 'ContactOrderPlacementLabel',
    ContactOrderPlacementSameAsPayment = 'ContactOrderPlacementSameAsPayment',
    ContactOrderPlacementName = 'ContactOrderPlacementName',
    ContactOrderPlacementLastName = 'ContactOrderPlacementLastName',
    ContactOrderPlacementEmail = 'ContactOrderPlacementEmail',
    ContactOrderPlacementPhone = 'ContactOrderPlacementPhone',
    ContactGoodsReceptionLabel = 'ContactGoodsReceptionLabel',
    ContactGoodsReceptionSameAsPayment = 'ContactGoodsReceptionSameAsPayment',
    ContactGoodsReceptionName = 'ContactGoodsReceptionName',
    ContactGoodsReceptionLastName = 'ContactGoodsReceptionLastName',
    ContactGoodsReceptionEmail = 'ContactGoodsReceptionEmail',
    ContactGoodsReceptionPhone = 'ContactGoodsReceptionPhone',
    AgreeToTerms = "AgreeToTerms"
}

export const renderInputSection = (
    resources: IVinhusetBusinessSignUpResources,
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
    onValidateInputs: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    showErrors: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (id: string) => (errors: boolean, currAddress?: Address) => void,
    termsAndConditions?: ILinkData
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const {
            id,
            type,
            label,
            disabled,
            maxChars,
            isRequired,
            pattern,
            customClass,
            value,
            fieldReference,
            placeholder,
            radioButtonOptions,
            radioButtonGroupClass
        } = element;

        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <SignUpError {...{ className, type: 'item', message: element.error }} />
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress?.(id),
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset,
                            dontShowRequiredMark: true,
                            hideLabels: true,
                            labelsAsPlaceholders: true
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.RadioButton) {
            baseSignUpItem.input = (
                <div className={radioButtonGroupClass}>
                    {radioButtonOptions &&
                        radioButtonOptions.map((element, index) => {
                            return <BusinessSignUpRadioButtonOption key={index} {...element}></BusinessSignUpRadioButtonOption>;
                        })}
                </div>
            );
        } else if (type === InputType.Select) {
            baseSignUpItem.input = (
                <select
                    id={id}
                    className={`${className}-select`}
                    onChange={onInputChange}
                >
                    <option value=''>{resources.selectChooseEmptyOptionLabel}</option>
                    {element.selectOptions?.map(option => (
                        <BusinessSignUpOption key={option.id} {...option} />
                    ))}
                </select>
            );
        } else if (type === InputType.Label) {
            baseSignUpItem.input = <label>{value}</label>;
        } else if (type === InputType.Checkbox) {
            baseSignUpItem.input = (
                <div style={{ display: 'flex', gap: 6 }}>
                    {radioButtonOptions && radioButtonOptions.map(option => (
                        <label key={option.id}>
                            <input
                                className={`${className}-checkbox`}
                                type='checkbox'
                                id={option.id}
                                name={option.name}
                                value={option.value}
                                data-form-item-id={id}
                                onChange={option.onChange}
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
            );
        } else if (type === InputType.AgreeToTerms) {
            baseSignUpItem.input = (
                <label>
                    <input
                        className={`${className}-checkbox`}
                        type='checkbox'
                        id={`${id}-yes`}
                        name={'business-sign-up-radio-button'}
                        value={'yes'}
                        onChange={onInputChange}
                        required={isRequired}
                        data-form-item-id={id}
                    />
                    <span>
                        {label.substring(0, label.indexOf('{0}'))}
                        <a href={termsAndConditions?.destinationUrl} target='_blank' rel="noreferrer">
                            {resources.agreeToTermsLinkLabel}
                        </a>
                        {label.substring(label.indexOf('{0}') + 3)}
                    </span>
                </label>
            );
        } else {
            baseSignUpItem.input = (
                <SignUpInput
                    {...{
                        id,
                        type,
                        value,
                        disabled,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : isRequired,
                        onChange: onInputChange,
                        onValidate: onValidateInputs,
                        fieldReference,
                        placeholder
                    }}
                />
            );
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IVinhusetBusinessSignUpProps<IVinhusetBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    private readonly moduleClassName: string = 'ms-business-sign-up';
    private readonly prospectRadioButtonName: string = 'business-sign-up-radio-button';

    // Used as default data
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.Department,
            type: InputType.Text,
            label: this.props.resources.departmentLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        },
        {
            id: InputID.ServingPlace,
            type: InputType.Text,
            label: this.props.resources.servingPlaceLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        },
        {
            id: InputID.ServingPlacePhone,
            type: InputType.Text,
            label: this.props.resources.servingPlacePhoneLabel,
            value: '',
            isRequired: true,
            pattern: PhoneRegex.defaultRegex,
            customClass: 'width-100'
        },
        {
            id: InputID.ServingPlaceEmail,
            type: InputType.Text,
            label: this.props.resources.servingPlaceEmailLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-100'
        },
        {
            id: InputID.DeliveryFrequency,
            // Include a sub label with text: "(Anslag)"
            type: InputType.Select,
            label: this.props.resources.deliveryFrequencyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            selectOptions: [
                {
                    id: `${InputID.DeliveryFrequency}-weekly`,
                    label: this.props.resources.deliveryFrequencyWeeklyLabel,
                    value: 'Ukentlig'
                },
                {
                    id: `${InputID.DeliveryFrequency}-every-other-week`,
                    label: this.props.resources.deliveryFrequencyBiWeeklyLabel,
                    value:'Annhver uke'
                },
                {
                    id: `${InputID.DeliveryFrequency}-every-third-week`,
                    label: this.props.resources.deliveryFrequencyThirdWeekLabel,
                    value: 'Hver tredje uke'
                },
                {
                    id: `${InputID.DeliveryFrequency}-monthly`,
                    label: this.props.resources.deliveryFrequencyMonthlyLabel,
                    value: 'Månedlig'
                }
            ]
        },
        {
            id: 'InvoiceAddressLabel',
            type: InputType.Label,
            label: this.props.resources.invoiceAddressLabel,
            isRequired: false,
            value: ''
        },
        {
            id: InputID.InvoiceAddress,
            type: InputType.Address,
            label: this.props.resources.invoiceAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.InvoiceType,
            type: InputType.Checkbox,
            label: this.props.resources.invoiceTypeLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            radioButtonOptions: [
                {
                    id: `${InputID.InvoiceType}-Email`,
                    name: this.prospectRadioButtonName,
                    value: 'E-post',
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    label: this.props.resources.invoiceTypeEmailLabel,
                    onChange: this._onInputChange.bind(this)
                },
                {
                    id: `${InputID.InvoiceType}-EHF`,
                    name: this.prospectRadioButtonName,
                    value: 'EHF',
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    label: this.props.resources.invoiceTypeEHFLabel,
                    onChange: this._onInputChange.bind(this)
                }
            ]
        },
        {
            id: 'DeliveryAddressLabel',
            type: InputType.Label,
            label: this.props.resources.deliveryAddressLabel,
            isRequired: false,
            value: '',
            customClass: 'width-100'
        },
        {
            id: InputID.DeliveryAddress,
            type: InputType.Address,
            label: this.props.resources.deliveryAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.DeliveryInformation,
            type: InputType.TextArea,
            label: this.props.resources.deliveryInformationLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.ContactPaymentLabel,
            type: InputType.Label,
            label: this.props.resources.contactPaymentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.ContactPaymentName,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.firstNameLabel,
            customClass: 'hide-label width-50'
        },
        {
            id: InputID.ContactPaymentLastName,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.lastNameLabel,
            customClass: 'hide-label width-50'
        },
        {
            id: InputID.ContactPaymentEmail,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            placeholder: this.props.resources.emailAddressLabel,
            customClass: 'hide-label width-100'
        },
        {
            id: InputID.ContactPaymentPhone,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.phoneLabelText,
            customClass: 'hide-label width-100'
        },
        {
            id: InputID.ContactOrderPlacementLabel,
            type: InputType.Label,
            label: this.props.resources.contactOrderPlacementLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.ContactOrderPlacementSameAsPayment,
            type: InputType.Checkbox,
            label: '-',
            value: '',
            isRequired: false,
            radioButtonOptions: [
                {
                    id: `${InputID.ContactOrderPlacementSameAsPayment}-yes`,
                    name: this.prospectRadioButtonName,
                    value: 'yes',
                    label: this.props.resources.sameAsPaymentContactLabel,
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    onChange: this._onInputChange.bind(this)
                }
            ],
            customClass: 'hide-label width-100'
        },
        {
            id: InputID.ContactOrderPlacementName,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.firstNameLabel,
            checkboxDisable: InputID.ContactOrderPlacementSameAsPayment,
            customClass: 'hide-label width-50'
        },
        {
            id: InputID.ContactOrderPlacementLastName,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.lastNameLabel,
            checkboxDisable: InputID.ContactOrderPlacementSameAsPayment,
            customClass: 'hide-label width-50'
        },
        {
            id: InputID.ContactOrderPlacementEmail,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            placeholder: this.props.resources.emailAddressLabel,
            checkboxDisable: InputID.ContactOrderPlacementSameAsPayment,
            customClass: 'hide-label width-100'
        },
        {
            id: InputID.ContactOrderPlacementPhone,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.phoneLabelText,
            checkboxDisable: InputID.ContactOrderPlacementSameAsPayment,
            customClass: 'hide-label width-100'
        },
        {
            id: InputID.ContactGoodsReceptionLabel,
            type: InputType.Label,
            label: this.props.resources.contactGoodsReceptionLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.ContactGoodsReceptionSameAsPayment,
            type: InputType.Checkbox,
            label: '-',
            value: '',
            isRequired: false,
            radioButtonOptions: [
                {
                    id: `${InputID.ContactGoodsReceptionSameAsPayment}-yes`,
                    name: this.prospectRadioButtonName,
                    value: 'yes',
                    label: this.props.resources.sameAsPaymentContactLabel,
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    onChange: this._onInputChange.bind(this)
                }
            ],
            customClass: 'hide-label width-100'
        },
        {
            id: InputID.ContactGoodsReceptionName,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.firstNameLabel,
            checkboxDisable: InputID.ContactGoodsReceptionSameAsPayment,
            customClass: 'hide-label width-50'
        },
        {
            id: InputID.ContactGoodsReceptionLastName,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.lastNameLabel,
            checkboxDisable: InputID.ContactGoodsReceptionSameAsPayment,
            customClass: 'hide-label width-50'
        },
        {
            id: InputID.ContactGoodsReceptionEmail,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            placeholder: this.props.resources.emailAddressLabel,
            checkboxDisable: InputID.ContactGoodsReceptionSameAsPayment,
            customClass: 'hide-label width-100'
        },
        {
            id: InputID.ContactGoodsReceptionPhone,
            type: InputType.Text,
            label: '-',
            value: '',
            isRequired: true,
            placeholder: this.props.resources.phoneLabelText,
            checkboxDisable: InputID.ContactGoodsReceptionSameAsPayment,
            customClass: 'hide-label width-100'
        },
        {
            id: InputID.AgreeToTerms,
            type: InputType.AgreeToTerms,
            label: this.props.resources.agreeToTermsLabel,
            value: '',
            isRequired: true,
            customClass: 'hide-label width-100'
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isReady, formItems } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;

        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                this.props.resources,
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange.bind(this),
                this._onValidateInputs.bind(this),
                showAddressErrors,
                addressForm,
                this._onAddressChange,
                this.props.config.termsAndConditionsLink
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={!isReady}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private _onInputChange (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void {
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.type === InputType.RadioButton && event.target.name === this.prospectRadioButtonName) {
                item.value = event.target.value;
            } else if (item.type === InputType.Checkbox && item.id === event.target.dataset.formItemId) {
                const oldValue = item.value.split(', ').filter(Boolean);
                let newValue: string[] | undefined;

                if ((event.target as HTMLInputElement).checked && !oldValue.includes(event.target.value)) {
                    newValue = [...oldValue, event.target.value];
                } else {
                    newValue = oldValue.filter(value => value !== event.target.value);
                }

                item.value = newValue.join(', ');
            } else if (item.type === InputType.AgreeToTerms && item.id === event.target.dataset.formItemId) {
                item.value = (event.target as HTMLInputElement).checked ? 'yes' : '';
            } else if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });

        newForm.forEach(item => {
            if (item.checkboxDisable) {
                // Find checkbox form item with id from checkboxDisable
                const checkboxItem = newForm.find(formItem => formItem.id === item.checkboxDisable);
                if (checkboxItem) {
                    // If checkbox is checked, disable the form item
                    item.disabled = checkboxItem.value === 'yes';
                    item.isRequired = !item.disabled;
                }
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    }

    private _onValidateInputs(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        const newForm = this.state.formItems;
        newForm.forEach(item => item.error = undefined);

        const paymentEmail = newForm.find(x => x.id === InputID.ContactPaymentEmail);
        const orderPlacementEmail = newForm.find(x => x.id === InputID.ContactOrderPlacementEmail);
        const goodsReceptionEmail = newForm.find(x => x.id === InputID.ContactGoodsReceptionEmail);

        if (orderPlacementEmail && orderPlacementEmail.value) {
            if (orderPlacementEmail.value === paymentEmail?.value) {
                orderPlacementEmail.error = this.props.resources.orderPlacementSameAsPaymentContactError;
            }/*  else if (orderPlacementEmail.value === goodsReceptionEmail?.value) {
                orderPlacementEmail.error = this.props.resources.orderPlacementSameAsGoodsReceptionContactError;
            } */
        }

        if (goodsReceptionEmail && goodsReceptionEmail.value) {
            if (goodsReceptionEmail.value === paymentEmail?.value) {
                goodsReceptionEmail.error = this.props.resources.goodsReceptionSameAsPaymentContactError;
            }/*  else if (goodsReceptionEmail.value === orderPlacementEmail?.value) {
                goodsReceptionEmail.error = this.props.resources.goodsReceptionSameAsOrderPlacementContactError;
            } */
        }

        this.setState({
            formItems: newForm
        });
    }

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (id: string) => {
        return (errors: boolean, currentAddress?: Address): void => {
            // Find address item
            const newForm = this.state.formItems;
            const addressItem = newForm.find(item => {
                return item.type === InputType.Address && item.id === id;
            });

            // If found --> set to currentAddress
            if (addressItem) {
                addressItem.address = currentAddress;

                // Update state with new values and set errors to false if address is not required field
                this.setState(
                    {
                        formItems: newForm,
                        addressHasErrors: errors,
                        showAddressErrors: false
                    },
                    () => {
                        this.setState({ isReady: this._isFormReady() });
                    }
                );
            }
        };
    };

    private readonly _generateComment = (): string => {
        const servingPlace = this._getItem(InputID.ServingPlace);
        const servingPlacePhone = this._getItem(InputID.ServingPlacePhone);
        const servingPlaceEmail = this._getItem(InputID.ServingPlaceEmail);
        const deliveryFrequency = this._getItem(InputID.DeliveryFrequency);
        const invoiceType = this._getItem(InputID.InvoiceType);
        const invoiceAddress = this._getItem(InputID.InvoiceAddress, true)?.address;
        const deliveryAddress = this._getItem(InputID.DeliveryAddress, true)?.address;
        const deliveryInformation = this._getItem(InputID.DeliveryInformation);
        const contactPaymentName = this._getItem(InputID.ContactPaymentName);
        const contactPaymentLastName = this._getItem(InputID.ContactPaymentLastName);
        const contactPaymentEmail = this._getItem(InputID.ContactPaymentEmail);
        const contactPaymentPhone = this._getItem(InputID.ContactPaymentPhone);
        const contactOrderPlacementSameAsPayment = this._getItem(InputID.ContactOrderPlacementSameAsPayment);
        const contactOrderPlacementName = this._getItem(InputID.ContactOrderPlacementName);
        const contactOrderPlacementLastName = this._getItem(InputID.ContactOrderPlacementLastName);
        const contactOrderPlacementEmail = this._getItem(InputID.ContactOrderPlacementEmail);
        const contactOrderPlacementPhone = this._getItem(InputID.ContactOrderPlacementPhone);
        const contactGoodsReceptionSameAsPayment = this._getItem(InputID.ContactGoodsReceptionSameAsPayment);
        const contactGoodsReceptionName = this._getItem(InputID.ContactGoodsReceptionName);
        const contactGoodsReceptionLastName = this._getItem(InputID.ContactGoodsReceptionLastName);
        const contactGoodsReceptionEmail = this._getItem(InputID.ContactGoodsReceptionEmail);
        const contactGoodsReceptionPhone = this._getItem(InputID.ContactGoodsReceptionPhone);

        let comment = `Navn på serveringsted: ${servingPlace?.value || ''}
Telefon serveringsted: ${servingPlacePhone?.value || ''}
E-post serveringsted: ${servingPlaceEmail?.value || ''}
Ønsket leveringsfrekvens: ${deliveryFrequency?.value || ''}
Ønsket faktura-type: ${invoiceType?.value || ''}
Leverings informasjon: ${deliveryInformation?.value || ''}

Fakturaadresse:
${invoiceAddress?.Street || ''}
${invoiceAddress?.City || ''} ${invoiceAddress?.ZipCode || ''}

Leveringsadresse:
${deliveryAddress?.Street || ''}
${deliveryAddress?.City || ''} ${deliveryAddress?.ZipCode || ''}

Betaling kontaktperson:
${contactPaymentName?.value || ''} ${contactPaymentLastName?.value || ''}
${contactPaymentEmail?.value || ''}
${contactPaymentPhone?.value || ''}`;

        if (contactOrderPlacementSameAsPayment?.value !== 'yes') {
            comment += `\n
Varebestilling kontaktperson:
${contactOrderPlacementName?.value || ''} ${contactOrderPlacementLastName?.value || ''}
${contactOrderPlacementEmail?.value || ''}
${contactOrderPlacementPhone?.value || ''}`;
        }

        if (contactGoodsReceptionSameAsPayment?.value !== 'yes') {
            comment += `\n
Varemottak kontaktperson:
${contactGoodsReceptionName?.value || ''} ${contactGoodsReceptionLastName?.value || ''}
${contactGoodsReceptionEmail?.value || ''}
${contactGoodsReceptionPhone?.value || ''}`;
        }

        return comment;
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern
            if (currentInput.error) {
                return false;
            }

            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            }
            if (currentInput.value && currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        return true;
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true
            });

            event.preventDefault();
            return;
        }

        const { config, context } = this.props;

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.ContactPaymentName);
        const lastName = this._getItem(InputID.ContactPaymentLastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyEmail = this._getItem(InputID.ContactPaymentEmail);
        const companyPhone = this._getItem(InputID.ContactPaymentPhone);
        const deliveryAddress = this._getItem(InputID.DeliveryAddress, true);
        const department = this._getItem(InputID.Department);

        const orderPlacementSameAsPayment = this._getItem(InputID.ContactOrderPlacementSameAsPayment)?.value === 'yes';
        const orderPlacementName = this._getItem(InputID.ContactOrderPlacementName);
        const orderPlacementLastName = this._getItem(InputID.ContactOrderPlacementLastName);
        const orderPlacementEmail = this._getItem(InputID.ContactOrderPlacementEmail);
        const orderPlacementPhone = this._getItem(InputID.ContactOrderPlacementPhone);

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail?.value && deliveryAddress?.address) {
            deliveryAddress.address.Email = orderPlacementSameAsPayment ? companyEmail.value : orderPlacementEmail?.value;
        }

        if (companyPhone?.value && deliveryAddress?.address) {
            deliveryAddress.address.Phone = orderPlacementSameAsPayment ? companyPhone.value : orderPlacementPhone?.value;
        }

        const newPartner: BusinessPartnerProspect = {
            FirstName: orderPlacementSameAsPayment ? firstName?.value : orderPlacementName?.value,
            LastName: orderPlacementSameAsPayment ? lastName?.value : orderPlacementLastName?.value,
            CompanyName: companyName?.value,
            Address: deliveryAddress?.address,
            Department: department?.value,
            Comments: this._generateComment(),
            ProspectTypeValue: ProspectType.Buyer
        };

        this.setState({
            isReady: false
        });

        // Create business partner request
        createProspectAsync({ callerContext: context.actionContext }, newPartner)
            .then(() => {
                const successPageURL =
                    (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                    getUrlSync('home', this.props.context.actionContext) ||
                    '';
                window.location.href = successPageURL;
            })
            .catch(() => {
                this.setState({
                    hasSubmitErrors: true
                });
            });
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };
}

export default BusinessSignUp;
